import _ from 'lodash';

import { ModuleConfig, ModuleType } from 'global-services/constants/OhsObject';
import { OhsUser } from 'user/OhsUser';
import { getModuleName } from 'user/OhsUserServices';
import OhsEnvUtils from 'global-components/env/OhsEnvUtils';

export interface ReportsRouteTypes {
  key?: string;
  title: string;
  url: string;
  permission: boolean;
}

const getPermissionByEnv = (envOptions: any): boolean => {
  return OhsEnvUtils.hasPermissionByEnv(envOptions);
};

const getModuleViewPermission = (user: OhsUser | undefined, module: ModuleConfig): boolean => {
  if (module === ModuleConfig.Correspondence) {
    return user?.configs[module]?.PERMISSIONS.view_roll === true;
  }
  if (module === ModuleConfig.Visitor) {
    return user?.configs[module]?.PERMISSIONS.site_view === true;
  }
  return user?.configs[module]?.PERMISSIONS.view === true;
};

const devOnlyEnvOptions = {
  showInProd: false,
  showInDev: true,
  showInMobileProd: false,
  showInMobileDev: false,
};

export const getReportsRouteList = (user: OhsUser | undefined, orgControl?: any) =>
  [
    {
      title: getModuleName(user?.configs, ModuleType.Action),
      url: `/reporting/superset/${ModuleConfig.Action}`,
      permission: getModuleViewPermission(user, ModuleConfig.Action),
    },
    {
      title: getModuleName(user?.configs, ModuleType.Asset),
      url: `/reporting/superset/${ModuleConfig.Asset}`,
      permission: getModuleViewPermission(user, ModuleConfig.Asset),
    },
    {
      title: getModuleName(user?.configs, ModuleType.Chemical),
      url: `/reporting/superset/${ModuleConfig.Chemical}`,
      permission:
        getPermissionByEnv(devOnlyEnvOptions) &&
        getModuleViewPermission(user, ModuleConfig.Chemical),
    },
    {
      title: getModuleName(user?.configs, ModuleType.CorporateRisk),
      url: `/reporting/superset/${ModuleConfig.CorporateRisk}`,
      permission: getModuleViewPermission(user, ModuleConfig.CorporateRisk),
    },
    {
      title: getModuleName(user?.configs, ModuleType.Correspondence),
      url: `/reporting/superset/${ModuleConfig.Correspondence}`,
      permission: getModuleViewPermission(user, ModuleConfig.Correspondence),
    },
    {
      title: getModuleName(user?.configs, ModuleType.Contractor),
      url: `/reporting/superset/${ModuleConfig.Contractor}`,
      permission: getModuleViewPermission(user, ModuleConfig.Contractor),
    },
    {
      title: getModuleName(user?.configs, ModuleType.HR),
      url: `/reporting/superset/${ModuleConfig.HR}`,
      permission: getModuleViewPermission(user, ModuleConfig.HR),
    },
    {
      title: getModuleName(user?.configs, ModuleType.Incident),
      url: `/reporting/superset/${ModuleConfig.Incident}`,
      permission: getModuleViewPermission(user, ModuleConfig.Incident),
    },
    {
      title: `${getModuleName(user?.configs, ModuleType.Incident)} Frequency`,
      url: `/reporting/superset/${ModuleConfig.Incident}_frequency`,
      permission:
        user?.configs.incident?.FREQUENCY_RATE_REPORTING === true &&
        user?.configs.incident?.PERMISSIONS.lti_captured_report_view === true &&
        orgControl &&
        orgControl.incidentLTI === true,
    },
    {
      title: getModuleName(user?.configs, ModuleType.Inspection),
      url: `/reporting/superset/${ModuleConfig.Inspection}`,
      permission: getModuleViewPermission(user, ModuleConfig.Inspection),
    },
    {
      title: getModuleName(user?.configs, ModuleType.ReviewPlan),
      url: `/reporting/superset/${ModuleConfig.ReviewPlan}`,
      permission:
        getPermissionByEnv(devOnlyEnvOptions) &&
        getModuleViewPermission(user, ModuleConfig.ReviewPlan),
    },
    {
      title: getModuleName(user?.configs, ModuleType.SafetyPlan),
      url: `/reporting/superset/${ModuleConfig.SafetyPlan}`,
      permission: getModuleViewPermission(user, ModuleConfig.SafetyPlan),
    },
    {
      title: _.startCase(`${ModuleConfig.Task}s`),
      url: `/reporting/superset/${ModuleConfig.Task}`,
      permission: true,
    },
    {
      title: _.startCase(`${ModuleConfig.Training}`),
      url: `/reporting/superset/${ModuleConfig.Training}`,
      permission: getModuleViewPermission(user, ModuleConfig.Training),
    },
    {
      title: _.startCase(`${ModuleConfig.Visitor}`),
      url: `/reporting/superset/${ModuleConfig.Visitor}`,
      permission: getModuleViewPermission(user, ModuleConfig.Visitor),
    },
  ] as ReportsRouteTypes[];

export const getBusinessIntelligenceRouteList = (user: OhsUser | undefined) =>
  [
    {
      title: _.startCase(`${ModuleConfig.Task}s`),
      url: `/reporting/powerbi/${ModuleConfig.Task}`,
      permission: true,
    },
    {
      title: getModuleName(user?.configs, ModuleType.Incident),
      url: '/reporting/powerbi/incident',
      permission: user?.configs.incident?.PERMISSIONS.view === true,
    },
    {
      title: getModuleName(user?.configs, ModuleType.Action),
      url: '/reporting/powerbi/action',
      permission: user?.configs.action?.PERMISSIONS.view === true,
    },
    {
      title: getModuleName(user?.configs, ModuleType.Inspection),
      url: '/reporting/powerbi/inspection',
      permission: user?.configs.inspection?.PERMISSIONS.view === true,
    },
  ] as ReportsRouteTypes[];
